import type { ActionItem, BusinessProfile, GeneratedDocument, LiteracyRecord, ModuleStatus, ReadinessScore, RiskAssessment, RoleCheckRecord, TransparencyCheck, AIUseCase, AITool } from "./types";
import { SCHEMA_VERSION } from "./types";
import { now, uid } from "./rules";

export function isRealLiteracyEvidence(item: LiteracyRecord) {
  return Boolean(item.completed_at && item.evidence_markdown && item.answers && Object.keys(item.answers).length >= 8);
}

export function computeScore(input: {
  profiles: BusinessProfile[];
  tools: AITool[];
  useCases: AIUseCase[];
  risks: RiskAssessment[];
  roles: RoleCheckRecord[];
  literacy: LiteracyRecord[];
  transparency: TransparencyCheck[];
  documents: GeneratedDocument[];
  actionItems: ActionItem[];
}): ReadinessScore {
  let score = 0;
  if (input.profiles.some((item) => item.onboarding_completed)) score += 10;
  if (input.tools.length > 0) score += 15;
  if (input.tools.length > 0 || input.profiles.some((item) => item.ai_usage_level === "low")) score += 10;
  if (input.useCases.length > 0) score += 15;
  if (input.risks.length > 0) score += 10;
  if (input.roles.length > 0) score += 10;
  if (input.literacy.some((item) => item.passed && item.self_confirmed && isRealLiteracyEvidence(item))) score += 10;
  if (input.transparency.length > 0) score += 10;
  if (input.documents.some((item) => item.type === "policy")) score += 5;
  if (input.documents.some((item) => item.type === "readiness_dossier" && item.status !== "draft")) score += 5;
  const red_points_count = input.risks.reduce((sum, item) => sum + item.red_review_points.length, 0);
  const openActions = input.actionItems.filter((item) => item.status !== "done" && item.status !== "postponed");
  const hasExternalReviewAction = openActions.some((item) => item.status === "needs_external_review");
  const scoreCap = red_points_count > 0 ? 80 : hasExternalReviewAction ? 85 : openActions.length > 0 ? 90 : 100;
  score = Math.min(score, scoreCap);
  return {
    id: "readiness_score_current",
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    score,
    red_points_count,
    explanation: scoreCap < 100 ? `Readiness-Dokumentationsfortschritt, keine Rechtskonformitäts- oder Zertifizierungsbewertung. Score aktuell auf ${scoreCap}/100 begrenzt, weil noch rote Prüfpunkte oder offene Maßnahmen bestehen.` : "Readiness-Dokumentationsfortschritt, keine Rechtskonformitäts- oder Zertifizierungsbewertung."
  };
}

export function moduleStatuses(input: {
  profiles: BusinessProfile[];
  tools: AITool[];
  useCases: AIUseCase[];
  roles: RoleCheckRecord[];
  literacy: LiteracyRecord[];
  transparency: TransparencyCheck[];
  documents: GeneratedDocument[];
  actionItems: ActionItem[];
}): ModuleStatus[] {
  const status = (module: string, complete: boolean, started: boolean, summary: string): ModuleStatus => ({
    id: `module_${module.toLowerCase().replaceAll(" ", "_").replaceAll("-", "_")}`,
    schema_version: SCHEMA_VERSION,
    created_at: now(),
    updated_at: now(),
    module,
    state: complete ? "complete" : started ? "in_progress" : "not_started",
    summary
  });
  const openActions = input.actionItems.filter((item) => item.status !== "done" && item.status !== "postponed");
  return [
    status("Profil / Onboarding", input.profiles.some((item) => item.onboarding_completed), input.profiles.length > 0, "Business-Kontext und Local-first-Hinweis"),
    status("Tool-Register", input.tools.length > 0, input.tools.length > 0, `${input.tools.length} Tools dokumentiert`),
    status("Use Cases", input.useCases.length > 0, input.useCases.length > 0, `${input.useCases.length} Use Cases dokumentiert`),
    status("Maßnahmen", openActions.length === 0 && input.actionItems.length > 0, input.actionItems.length > 0, openActions.length ? `${openActions.length} offene Maßnahmen` : input.actionItems.length ? "Alle Maßnahmen abgeschlossen oder zurückgestellt" : "Noch keine Maßnahmen abgeleitet"),
    status("Rollen-Check", input.roles.length > 0, input.roles.length > 0, "Rollenprofil A-D"),
    status("AI-Literacy", input.literacy.some((item) => item.passed && isRealLiteracyEvidence(item)), input.literacy.some(isRealLiteracyEvidence), "Basisnachweis und Selbstbestätigung"),
    status("Transparenz", input.transparency.length > 0, input.transparency.length > 0, "Hinweisbedarf geprüft"),
    status("Dokumente", input.documents.length > 0, input.documents.length > 0, "Summary, Policy und Hinweise")
  ];
}
